import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

// Amplify
import { signOut } from "aws-amplify/auth";

// Store Redux
import { clearTokens } from "@/redux/actions";
import { actions } from "@/redux/reducers/User";
import { actions as actionsLocation } from "@/redux/reducers/Location";

// Components
import HeaderContainer, { HeaderContainerProps } from "./components/HeaderContainer";

import { RootState } from "@/redux/store";
import { logout } from "@/Auth/api/Logout/mutation";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { gql } from "@/services/httpAdapter";
import { ON_UPDATE_SYSTEM_SETTINGS_SUBSCRIPTION } from "@/Settings/api/SystemSettings/subscriptions";
import { useCommissionCycleStatusSubscriptions } from "@/Common/hooks/CommissionCycle";

export default function Header(props: Readonly<HeaderContainerProps>) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const { addSubscription, getSubscription } = useSubscriptions();

	const { mutateAsync } = useMutation({
		mutationKey: ["logout"],
		mutationFn: async (input: { locationId: string; userId: string }) => logout(input),
	});

	const localUser = useSelector((state: RootState) => state.user.user);
	const locationId = localUser?.locationId ?? "";
	const userId = localUser?._id ?? "";

	const { locationId: locationIdSelected } = useSelector((state: RootState) => state.location) ?? {};

	const onIdle = useCallback(() => {
		queryClient.clear();
		dispatch(actions.SET_USER({ user: null }));
		dispatch(actionsLocation.SET_LOCATION({ location: null }));
		dispatch(clearTokens());
	}, [dispatch, localUser, queryClient]);

	const handleLogout = async () => {
		if (localUser) {
			await mutateAsync({ locationId, userId });
			await signOut({ global: true });
			onIdle();
			window.localStorage.clear();
			navigate("/login");
		}
	};

	const onLogoutSubscription = getSubscription(["onLogout", locationId, userId]);
	onLogoutSubscription?.subscriptions?.onLogout?.setCallback((result: { data: { onLogout: { userId: string } } }) => {
		if (result.data.onLogout.userId === userId) {
			onIdle();
			window.localStorage.clear();
			navigate("/login");
		}
	});

	useCommissionCycleStatusSubscriptions({ locationId: locationIdSelected });

	useEffect(() => {
		if (locationId) {
			addSubscription(
				["onLogout", locationId, userId],
				[
					{
						context: "onLogout",
						query: gql`
							subscription onLogout($userId: String) {
								onLogout(userId: $userId) {
									locationId
									userId
								}
							}
						`,
						variables: {
							userId,
						},
					},
				],
				true
			);
			addSubscription(
				["systemSettingsSubscription", locationId],
				[
					{
						context: "onUpdateSystemSettings",
						query: ON_UPDATE_SYSTEM_SETTINGS_SUBSCRIPTION,
						variables: {
							locationId,
						},
					},
				],
				true
			);
		}
	}, [locationId, userId]);

	return (
		<div className="header-style">
			<HeaderContainer {...props} onLogoutAction={handleLogout} />
		</div>
	);
}
